import { publicClient } from '@/core/publicClient';
import { useQuery } from '@tanstack/react-query';

const SNAPSHOT_KEY = 'lookups_snapshot';
const SNAPSHOT_ETAG_KEY = 'lookups_etag';

export interface LookupItem {
  id: number;
  code?: string;
  label?: string;
  name?: string;
  [key: string]: any;
}

export interface LookupsSnapshot {
  species?: LookupItem[];
  sexes?: LookupItem[];
  sizes?: LookupItem[];
  statuses?: LookupItem[];
  colors?: LookupItem[];
  breeds?: LookupItem[];
  temperaments?: LookupItem[];
  cities?: LookupItem[];
  [key: string]: any;
}

async function fetchSnapshot(): Promise<{ data: LookupsSnapshot; etag?: string }> {
  const storedRaw = sessionStorage.getItem(SNAPSHOT_KEY);
  const stored = storedRaw ? JSON.parse(storedRaw) : undefined;
  const storedEtag = sessionStorage.getItem(SNAPSHOT_ETAG_KEY) || undefined;

  // Se o cache não tem cidades, forçamos refresh (sem If-None-Match)
  const forceRefresh = !stored?.cities || stored.cities.length === 0;

  const headers: Record<string, string> = {};
  if (!forceRefresh && storedEtag) headers['If-None-Match'] = storedEtag;

  try {
    const res = await publicClient.get('/lookups/snapshot', { params: { includeCities: true }, headers, validateStatus: () => true });
    if (res.status === 304) {
      return { data: stored || {}, etag: storedEtag };
    }
    const newEtag = res.headers?.etag as string | undefined;
    const payload = res.data?.data || res.data || {};
    if (newEtag) sessionStorage.setItem(SNAPSHOT_ETAG_KEY, newEtag);
    sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(payload));
    return { data: payload, etag: newEtag };
  } catch (_) {
    // On failure, fallback to stored
    return { data: stored || {}, etag: storedEtag };
  }
}

export function useLookupsSnapshot() {
  return useQuery({
    queryKey: ['lookups-snapshot'],
    queryFn: async () => {
      const { data } = await fetchSnapshot();
      return data as LookupsSnapshot;
    },
  });
}

export function toOptionLabel(item: LookupItem): string {
  return (item.label || item.name || String(item.id)) as string;
}
